// src/pages/notifications/NotificationsDetailPage.tsx
// Full notification detail view with delivery attempts.

import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useNotifications, useNotificationsAnalytic, useI18n } from '@react/hooks';
import { Button, Card, CardHeader, CardTitle, CardContent, Alert, Badge, ErrorAlert,
} from '@react/components';
import { StatusBadge } from '../../components/StatusBadge';
import { formatNotificationStatus, formatChannelType, formatPriority, formatCategory, formatDeliveryAttempt } from '../../features/notifications/logic';
import { NotificationIcon } from '../../components/NotificationIcon';
import type { Notification } from '@core/types/notifications/Notification';

export const NotificationsDetailPage = observer(() => {
  const store = useNotifications();
  const analyticStore = useNotificationsAnalytic();
  const { t } = useI18n();
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    if (id) store.GetNotification(id);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const [markingRead, setMarkingRead] = useState(false);
  const [deliveryReport, setDeliveryReport] = useState<Awaited<ReturnType<typeof analyticStore.GetDeliveryReport>> | null>(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const item = store.currentItem as Notification | null;
  if (store.isLoadingItem) return <div className="py-8 text-center text-muted-foreground">{t('common.loading')}</div>;
  if (!item) return <Alert variant="error">{t('common.notFound')}</Alert>;

  const handleMarkRead = async () => {
    setMarkingRead(true);
    try {
      await store.SetNotificationRead(item.entity?.id ?? '', {} as never);
      if (id) store.GetNotification(id);
    } catch {
      // handled by store
    } finally {
      setMarkingRead(false);
    }
  };

  // Refresh a fuller delivery report from provider-confirmed data
  // (notifications.GetDeliveryReport) — a richer, authoritative view than
  // the delivery_attempts already embedded on the Notification itself.
  const handleLoadDeliveryReport = async () => {
    if (!item.entity?.id) return;
    setIsLoadingReport(true);
    try {
      const res = await analyticStore.GetDeliveryReport(item.entity.id);
      setDeliveryReport(res);
    } catch {
      // handled by store
    } finally {
      setIsLoadingReport(false);
    }
  };

  const isUnread = item.status !== 'NOTIFICATION_STATUS_READ';

  const status = formatNotificationStatus(item.status ?? '');
  const channel = formatChannelType(item.channel_type ?? '');
  const priority = formatPriority(item.priority ?? '');
  const category = formatCategory(item.category ?? '');

  const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
    <div className="py-3 sm:grid sm:grid-cols-3 sm:gap-4">
      <dt className="text-sm font-medium text-muted-foreground">{label}</dt>
      <dd className={`mt-1 text-sm text-foreground sm:col-span-2 sm:mt-0 ${mono ? 'font-mono' : ''}`}>{value || t('common.notSet')}</dd>
    </div>
  );

  const attempts = item.delivery_attempts?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/notifications" className="text-sm text-primary hover:text-primary/80">{t('common.backToList')}</Link>
          <h1 className="text-2xl font-semibold text-foreground mt-1">{item.subject || t('notifications.details')}</h1>
        </div>
        {isUnread && (
          <Button variant="secondary" onClick={handleMarkRead} isLoading={markingRead}>{t('notifications.markAsRead')}</Button>
        )}
      </div>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <CardHeader><CardTitle>{t('notifications.details')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y divide-border">
            <Field label={t('common.id')} value={item.entity?.id} mono />
            <Field label={t('notifications.subject')} value={item.subject} />
            <Field label={t('notifications.content')} value={<pre className="whitespace-pre-wrap text-sm">{item.content}</pre>} />
            <Field label={t('common.status')} value={<StatusBadge variant={status.variant} label={t(status.labelKey)} />} />
            <Field label={t('notifications.channelType')} value={<span className="inline-flex items-center gap-1.5"><NotificationIcon name={channel.icon} className="h-4 w-4" />{t(channel.labelKey)}</span>} />
            <Field label={t('notifications.priority')} value={<StatusBadge variant={priority.variant} label={t(priority.labelKey)} />} />
            <Field label={t('notifications.category')} value={t(category.labelKey)} />
            <Field label={t('notifications.recipient')} value={item.recipient?.display_name || item.recipient?.entity?.id} />
            <Field label={t('notifications.sender')} value={item.sender?.display_name || item.sender?.entity?.id} />
            <Field label={t('notifications.eventType')} value={item.event_type} />
            <Field label={t('notifications.templateId')} value={item.template_id} mono />
            <Field label={t('notifications.channelId')} value={item.channel_id} mono />
            <Field label={t('notifications.correlationId')} value={item.correlation_id} mono />
            <Field label={t('notifications.sourceService')} value={item.source_service} />
            <Field label={t('notifications.requiresConfirmation')} value={<Badge variant={item.requires_delivery_confirmation ? 'success' : 'secondary'}>{item.requires_delivery_confirmation ? t('common.yes') : t('common.no')}</Badge>} />
            <Field label={t('common.createdAt')} value={item.created_at} />
            <Field label={t('notifications.sentAt')} value={item.sent_at} />
            <Field label={t('notifications.deliveredAt')} value={item.delivered_at} />
            <Field label={t('notifications.readAt')} value={item.read_at} />
            {item.scheduled_at && <Field label={t('notifications.scheduledAt')} value={item.scheduled_at} />}
          </dl>
        </CardContent>
      </Card>

      {attempts.length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.deliveryAttempts')} ({attempts.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-3">
              {attempts.map((attempt, idx) => {
                const st = formatDeliveryAttempt(attempt);
                return (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                    <StatusBadge variant={st.variant} label={t(st.statusKey)} size="sm" />
                    <div className="flex-1 text-sm">
                      {attempt.error_message && <p className="text-error">{attempt.error_message}</p>}
                      {attempt.provider_response && <p className="text-muted-foreground font-mono text-xs mt-1">{attempt.provider_response}</p>}
                      <p className="text-muted-foreground text-xs mt-1">{attempt.attempted_at}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="flex items-center justify-between">
          <CardTitle>{t('notifications.deliveryReport.title')}</CardTitle>
          <Button variant="secondary" size="sm" onClick={handleLoadDeliveryReport} isLoading={isLoadingReport}>
            {t('notifications.deliveryReport.refresh')}
          </Button>
        </CardHeader>
        <CardContent>
          {!deliveryReport ? (
            <p className="text-sm text-muted-foreground">{t('notifications.deliveryReport.empty')}</p>
          ) : (deliveryReport.delivery_attempts?.items ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('notifications.deliveryAttempts')}: 0</p>
          ) : (
            <div className="space-y-3">
              {(deliveryReport.delivery_attempts?.items ?? []).map((attempt, idx) => {
                const st = formatDeliveryAttempt(attempt);
                return (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg border border-border">
                    <StatusBadge variant={st.variant} label={t(st.statusKey)} size="sm" />
                    <div className="flex-1 text-sm">
                      {attempt.error_message && <p className="text-error">{attempt.error_message}</p>}
                      {attempt.provider_response && <p className="text-muted-foreground font-mono text-xs mt-1">{attempt.provider_response}</p>}
                      <p className="text-muted-foreground text-xs mt-1">{attempt.attempted_at}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {item.metadata && Object.keys(item.metadata).length > 0 && (
        <Card>
          <CardHeader><CardTitle>{t('common.metadata')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(item.metadata, null, 2)}</pre></CardContent>
        </Card>
      )}

      {item.resources && (
        <Card>
          <CardHeader><CardTitle>{t('notifications.resources')}</CardTitle></CardHeader>
          <CardContent><pre className="text-xs text-muted-foreground overflow-auto">{JSON.stringify(item.resources, null, 2)}</pre></CardContent>
        </Card>
      )}
    </div>
  );
});

export default NotificationsDetailPage;
